export const translations = {
  en: {
    // Navbar
    navbar: {
      backToHome: 'Back to Home',
      overview: 'Overview',
      problem: 'Problem',
      solution: 'Solution',
      workflow: 'Workflow',
      ai: 'AI & Automation',
      technical: 'Technical',
      conclusion: 'Conclusion',
    },
    // HeroSection
    hero: {
      title: 'Canva China User Support',
      subtitle: 'Experience Upgrade',
      name: 'Ivy (Yuwei) Xie - Technical Project Manager & East-West Technology Bridge',
      description: 'Integrated Support Portal: Bridging WeChat and Zendesk',
      description2: 'for Seamless Customer Experience',
    },
    // ProblemSection
    problem: {
      title: 'Why Specialized Support Experience',
      title2: 'for Chinese Users?',
      description: 'Understanding the unique challenges in supporting Chinese users',
      channelMisalignment: {
        title: 'Channel Misalignment',
        description: 'Chinese users prefer WeChat, but support tickets are managed via email in Zendesk, creating communication barriers and user friction.',
      },
      fragmentedExperience: {
        title: 'Fragmented Experience',
        description: 'Poor ticket flow between WeChat and Zendesk systems leads to delayed responses and inconsistent support quality.',
      },
      collaborationBarriers: {
        title: 'Collaboration Barriers',
        description: 'Global support teams struggle to understand context and cultural nuances in Chinese user communications.',
      },
    },
    // SolutionSection
    solution: {
      title: 'Our Solution: Bridge Architecture',
      description: 'Seamlessly connecting WeChat users with Zendesk support infrastructure',
      realWorldTitle: 'Solution Based on Real-World Experience',
      realWorldDescription: 'This architecture design draws from my successful experience leading WeChat ecosystem integration at iHerb:',
      completeIntegration: {
        title: 'Complete API Integration',
        description: 'Led end-to-end WeChat API integration with global e-commerce systems including user identity, product catalog, order management, and payment processing',
      },
      socialFeatures: {
        title: 'Social Features Design',
        description: 'Designed "Share to Moments" social functionality, significantly increasing user-generated content and customer acquisition efficiency',
      },
      crossCultural: {
        title: 'Cross-Cultural Coordination',
        description: 'Served as technical bridge coordinating US-China teams, ensuring architecture meets both global security standards and local market requirements',
      },
    },
    // WorkflowSection
    workflow: {
      title: 'End-to-End User Experience Journey',
      description: 'Interactive demonstration of the complete support workflow',
      note: 'This interactive demo is based on my real-world experience handling cross-cultural technical integrations at e-commerce platforms, demonstrating the ability to transform complex cross-cultural support processes into seamless user experiences.',
      step1: {
        title: 'User Sends Message via WeChat',
        description: 'User initiates support request via WeChat',
      },
      step2: {
        title: 'Message Processing',
        description: 'System processes and categorizes the message',
      },
      step3: {
        title: 'Ticket Creation in Zendesk',
        description: 'Ticket is created in Zendesk with full context',
      },
      step4: {
        title: 'Agent Reviews and Responds',
        description: 'Support agent reviews ticket and prepares response',
      },
      step5: {
        title: 'Response Delivered via WeChat',
        description: 'Agent response is delivered back to user via WeChat',
      },
      step6: {
        title: 'User Follow-up',
        description: 'User can continue the conversation seamlessly',
      },
      step7: {
        title: 'Issue Resolved',
        description: 'Ticket is resolved and user receives confirmation',
      },
      wechatSupport: 'Canva Support',
      wechatOnline: 'Online',
      wechat: 'WeChat',
      zendeskSupport: 'Zendesk Support',
      zendeskAdmin: 'Admin Portal',
      typeMessage: 'Type a message...',
      noTickets: 'No tickets yet. Waiting for user message...',
      ticketId: 'ID',
      ticketSubject: 'Subject',
      ticketStatus: 'Status',
      ticketPriority: 'Priority',
      ticketSource: 'Source',
      ticketDetails: 'Ticket Details',
      created: 'Created',
      assignedTo: 'Assigned to',
      lastUpdated: 'Last updated',
      resolved: 'Resolved',
      conversationHistory: 'Conversation History',
      userViaWeChat: 'User (via WeChat)',
      supportAgent: 'Support Agent',
      previous: 'Previous',
      nextStep: 'Next Step',
      step: 'Step',
      new: 'New',
      open: 'Open',
      pending: 'Pending',
      solved: 'Solved',
      closed: 'Closed',
      urgent: 'Urgent',
      high: 'High',
      normal: 'Normal',
      low: 'Low',
    },
    // AISection
    ai: {
      title: 'Intelligence-Driven Efficiency',
      description: 'Leveraging AI and automation to enhance support quality and speed',
      autoClassification: {
        title: 'AI Auto Classification',
        description: 'Automatically categorize incoming messages using NLP and machine learning, routing them to the most appropriate support team.',
      },
      smartReply: {
        title: 'Smart Reply Suggestions',
        description: 'Provide intelligent reply suggestions to agents based on ticket context, reducing response time and ensuring consistency.',
      },
      fasterResponse: 'Faster Response Time',
      classificationAccuracy: 'Classification Accuracy',
      reducedWorkload: 'Reduced Workload',
    },
    // TechnicalSection
    technical: {
      title: 'Technical Highlights',
      description: 'Behind the scenes: Technology stack and implementation details',
      qualifications: 'Professional Qualifications & Technical Capabilities',
      certifications: 'Professional Certifications',
      pmpCertified: 'PMP Certified Project Manager - Proficient in Agile, Hybrid project management methodologies',
      bilingual: 'Bilingual Technical Communication - Fluent in coordinating global teams and Chinese suppliers',
      dualMasters: 'Dual Master\'s Degrees - University of Sydney: IT & IT Management',
      techStack: 'Technical Stack & Tools',
      realWorldStack: 'Real-World Tech Stack: API Integration, CI/CD Pipelines, Cloud Environments, React.js & .NET Core, SQL',
      toolEcosystem: 'Tool Ecosystem: Jira, Confluence, Zendesk, Git, Figma',
      experience: '5 Years Technical PM + 3 Years Software Development background',
      techStackTitle: 'Technology Stack',
      backendServer: 'Backend API server',
      messageStorage: 'Message storage',
      cachingLayer: 'Caching layer',
      officialWeChatApi: 'Official WeChat API',
      zendeskRestApi: 'Zendesk REST API',
      apiExample: 'API Integration Example',
      apiExampleNote: 'Based on Real-World Experience: This code example is inspired by my actual work integrating WeChat API with global e-commerce systems at iHerb.',
      systemArchitecture: 'System Architecture',
      microservices: 'Microservices architecture for scalability',
      messageQueue: 'Message queue for async processing (RabbitMQ)',
      restfulApi: 'RESTful API design for external integrations',
      realtimeSync: 'Real-time synchronization between systems',
      integrationPoints: 'Integration Points',
      wechatIntegration: 'WeChat Integration',
      wechatIntegrationDesc: 'Official WeChat Work API for enterprise messaging',
      zendeskIntegration: 'Zendesk Integration',
      zendeskIntegrationDesc: 'Zendesk REST API v2 for ticket management',
    },
    // ConclusionSection
    conclusion: {
      title: 'Value Created',
      description: 'Expected value based on past achievements and measurable impact',
      expectedValue: 'Expected Value Based on Past Achievements',
      expectedValueDesc: 'The following metrics are projections based on my proven track record of delivering measurable results in similar technical integration projects.',
      complaintReduction: 'Customer Complaint Reduction',
      complaintReductionDesc: 'Reference: Achieved at F&H through digital support tools',
      efficiencyImprovement: 'Operational Efficiency Improvement',
      efficiencyImprovementDesc: 'Reference: Achieved at F&H through workflow digitization, on-time delivery increased from 85% to 95%',
      complianceAssurance: 'Compliance Assurance',
      complianceAssuranceDesc: 'Reference: Ensured technical solutions meet both global security standards and Chinese data compliance at iHerb',
      keyAchievements: 'Key Achievements',
      achievement1: 'Seamless integration between WeChat and Zendesk',
      achievement2: 'Improved cross-team collaboration and understanding',
      achievement3: 'Enhanced user experience for Chinese market',
      achievement4: 'Scalable architecture for future growth',
      achievement5: 'AI-powered automation for efficiency',
      whyFit: 'Why I\'m the Right Fit for Canva',
      uniqueBridge: {
        title: 'Unique East-West Technology Bridge',
        description: 'Combining Western project management certification with real-world Chinese technology ecosystem experience',
      },
      provenTrack: {
        title: 'Proven Track Record',
        description: 'Led complex WeChat ecosystem integration projects with global platforms, delivering quantifiable results',
      },
      deepUnderstanding: {
        title: 'Deep Understanding',
        description: 'Master the art of balancing Chinese user needs with global enterprise standards',
      },
      completeCapability: {
        title: 'Complete Capability',
        description: 'Technical expertise + Business insight + Cultural understanding + Project management = Exceptional experience for Canva\'s Chinese users',
      },
      ctaTitle: 'Ready to Transform Canva\'s China User Support Experience?',
      ctaDescription: 'I\'m excited to bring my unique combination of technical expertise, project management skills, and cross-cultural understanding to help Canva deliver exceptional support experiences for Chinese users.',
      getInTouch: 'Get in Touch',
      viewLinkedIn: 'View LinkedIn Profile',
    },
  },
  zh: {
    // Navbar
    navbar: {
      backToHome: '返回首页',
      overview: '概述',
      problem: '问题',
      solution: '解决方案',
      workflow: '工作流程',
      ai: 'AI 与自动化',
      technical: '技术',
      conclusion: '总结',
    },
    // HeroSection
    hero: {
      title: 'Canva 中国用户支持',
      subtitle: '体验升级',
      name: '解雨微 (Ivy) - 技术项目经理 & 东西方技术桥梁',
      description: '集成支持门户：连接微信和 Zendesk',
      description2: '打造无缝客户体验',
    },
    // ProblemSection
    problem: {
      title: '为什么需要为中国用户',
      title2: '提供专业支持体验？',
      description: '了解支持中国用户面临的独特挑战',
      channelMisalignment: {
        title: '渠道不匹配',
        description: '中国用户偏好微信，但支持工单通过 Zendesk 的电子邮件管理，造成了沟通障碍和用户摩擦。',
      },
      fragmentedExperience: {
        title: '体验碎片化',
        description: '微信和 Zendesk 系统之间的工单流转不畅，导致响应延迟和支持质量不一致。',
      },
      collaborationBarriers: {
        title: '协作障碍',
        description: '全球支持团队难以理解中国用户沟通中的上下文和文化细微差别。',
      },
    },
    // SolutionSection
    solution: {
      title: '我们的解决方案：桥接架构',
      description: '无缝连接微信用户与 Zendesk 支持基础设施',
      realWorldTitle: '基于真实经验的解决方案',
      realWorldDescription: '此架构设计源于我在 iHerb 成功领导微信生态系统集成的经验：',
      completeIntegration: {
        title: '完整的 API 集成',
        description: '领导端到端的微信 API 与全球电商系统集成，包括用户身份、产品目录、订单管理和支付处理',
      },
      socialFeatures: {
        title: '社交功能设计',
        description: '设计了"分享到朋友圈"社交功能，显著增加了用户生成内容和客户获取效率',
      },
      crossCultural: {
        title: '跨文化协调',
        description: '作为技术桥梁协调中美团队，确保架构既符合全球安全标准，又满足本地市场需求',
      },
    },
    // WorkflowSection
    workflow: {
      title: '端到端用户体验旅程',
      description: '完整支持工作流程的交互式演示',
      note: '此交互式演示基于我在电商平台处理跨文化技术集成的真实经验，展示了将复杂的跨文化支持流程转化为无缝用户体验的能力。',
      step1: {
        title: '用户通过微信发送消息',
        description: '用户通过微信发起支持请求',
      },
      step2: {
        title: '消息处理',
        description: '系统处理并分类消息',
      },
      step3: {
        title: '在 Zendesk 创建工单',
        description: '在 Zendesk 中创建包含完整上下文的工单',
      },
      step4: {
        title: '客服审核并回复',
        description: '支持客服审核工单并准备回复',
      },
      step5: {
        title: '通过微信交付回复',
        description: '客服回复通过微信返回给用户',
      },
      step6: {
        title: '用户跟进',
        description: '用户可以无缝继续对话',
      },
      step7: {
        title: '问题已解决',
        description: '工单已解决，用户收到确认',
      },
      wechatSupport: 'Canva 支持',
      wechatOnline: '在线',
      wechat: '微信',
      zendeskSupport: 'Zendesk 支持',
      zendeskAdmin: '管理门户',
      typeMessage: '输入消息...',
      noTickets: '暂无工单。等待用户消息...',
      ticketId: 'ID',
      ticketSubject: '主题',
      ticketStatus: '状态',
      ticketPriority: '优先级',
      ticketSource: '来源',
      ticketDetails: '工单详情',
      created: '创建时间',
      assignedTo: '分配给',
      lastUpdated: '最后更新',
      resolved: '已解决',
      conversationHistory: '对话历史',
      userViaWeChat: '用户 (通过微信)',
      supportAgent: '支持客服',
      previous: '上一步',
      nextStep: '下一步',
      step: '步骤',
      new: '新建',
      open: '开启',
      pending: '待处理',
      solved: '已解决',
      closed: '已关闭',
      urgent: '紧急',
      high: '高',
      normal: '普通',
      low: '低',
    },
    // AISection
    ai: {
      title: '智能驱动效率',
      description: '利用 AI 和自动化提升支持质量和速度',
      autoClassification: {
        title: 'AI 自动分类',
        description: '使用自然语言处理和机器学习自动分类传入消息，将其路由到最合适的支持团队。',
      },
      smartReply: {
        title: '智能回复建议',
        description: '根据工单上下文为客服提供智能回复建议，减少响应时间并确保一致性。',
      },
      fasterResponse: '更快的响应时间',
      classificationAccuracy: '分类准确率',
      reducedWorkload: '减少工作量',
    },
    // TechnicalSection
    technical: {
      title: '技术亮点',
      description: '幕后：技术栈和实施细节',
      qualifications: '专业资质与技术能力',
      certifications: '专业认证',
      pmpCertified: 'PMP 认证项目经理 - 精通敏捷、混合项目管理方法论',
      bilingual: '双语技术沟通 - 熟练协调全球团队和中国供应商',
      dualMasters: '双硕士学位 - 悉尼大学：IT 与 IT 管理',
      techStack: '技术栈与工具',
      realWorldStack: '真实世界技术栈：API 集成、CI/CD 流水线、云环境、React.js 和 .NET Core、SQL',
      toolEcosystem: '工具生态系统：Jira、Confluence、Zendesk、Git、Figma',
      experience: '5 年技术项目经理 + 3 年软件开发背景',
      techStackTitle: '技术栈',
      backendServer: '后端 API 服务器',
      messageStorage: '消息存储',
      cachingLayer: '缓存层',
      officialWeChatApi: '官方微信 API',
      zendeskRestApi: 'Zendesk REST API',
      apiExample: 'API 集成示例',
      apiExampleNote: '基于真实经验：此代码示例来源于我在 iHerb 将微信 API 与全球电商系统集成的实际工作。',
      systemArchitecture: '系统架构',
      microservices: '用于可扩展性的微服务架构',
      messageQueue: '用于异步处理的消息队列 (RabbitMQ)',
      restfulApi: '用于外部集成的 RESTful API 设计',
      realtimeSync: '系统之间的实时同步',
      integrationPoints: '集成点',
      wechatIntegration: '微信集成',
      wechatIntegrationDesc: '用于企业消息的官方微信企业 API',
      zendeskIntegration: 'Zendesk 集成',
      zendeskIntegrationDesc: '用于工单管理的 Zendesk REST API v2',
    },
    // ConclusionSection
    conclusion: {
      title: '创造的价值',
      description: '基于过往成就和可衡量影响的预期价值',
      expectedValue: '基于过往成就的预期价值',
      expectedValueDesc: '以下指标基于我在类似技术集成项目中交付可衡量结果的已验证记录。',
      complaintReduction: '客户投诉减少',
      complaintReductionDesc: '参考：通过数字支持工具在 F&H 实现',
      efficiencyImprovement: '运营效率提升',
      efficiencyImprovementDesc: '参考：通过工作流程数字化在 F&H 实现，准时交付率从 85% 提升至 95%',
      complianceAssurance: '合规保证',
      complianceAssuranceDesc: '参考：在 iHerb 确保技术解决方案既符合全球安全标准，又符合中国数据合规要求',
      keyAchievements: '关键成就',
      achievement1: '微信与 Zendesk 之间的无缝集成',
      achievement2: '改善跨团队协作和理解',
      achievement3: '提升中国市场的用户体验',
      achievement4: '面向未来增长的可扩展架构',
      achievement5: 'AI 驱动的自动化提升效率',
      whyFit: '为什么我适合 Canva',
      uniqueBridge: {
        title: '独特的东西方技术桥梁',
        description: '将西方项目管理认证与真实的中国技术生态系统经验相结合',
      },
      provenTrack: {
        title: '已验证的记录',
        description: '领导复杂的微信生态系统与全球平台集成项目，交付可量化的成果',
      },
      deepUnderstanding: {
        title: '深刻理解',
        description: '掌握平衡中国用户需求与全球企业标准的艺术',
      },
      completeCapability: {
        title: '完整能力',
        description: '技术专长 + 商业洞察 + 文化理解 + 项目管理 = 为 Canva 中国用户提供卓越体验',
      },
      ctaTitle: '准备改变 Canva 的中国用户支持体验了吗？',
      ctaDescription: '我很兴奋能够将我独特的技术专长、项目管理技能和跨文化理解带到 Canva，帮助为中国用户提供卓越的支持体验。',
      getInTouch: '联系我',
      viewLinkedIn: '查看 LinkedIn 个人资料',
    },
  },
};

